import { isAfter } from 'date-fns';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { Browser } from '@capacitor/browser';
import { NavController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { filter, findIndex, includes, keys, map, range, shuffle } from 'lodash';
import { catchError, interval, Observable, of, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ChangeDetectorRef, Component, ElementRef, Inject, NgZone, Renderer2, ViewChild } from '@angular/core';
import { MobileAuthService, MobileQuestsService, MobileReputationService, MobileThemeService, ToasterService } from '@sifca-monorepo/clients';
import {
  QuestType,
  PictureType,
  QuestionTypeEnum,
  QuestActivityType,
  QuestPerformedType,
  WidgetIntegrationType,
  QuestActionDefinitionVideo,
  ReputationWithoutTargetType,
  QuestionWithResponsesStatsType,
  QuestActionDefinitionDefinitionApiParamsType,
} from '@sifca-monorepo/widget-generator';

declare const FB: any;
declare const YT: any;

@Component({
  template: '',
})
export class MobileQuestDetailsComponent {
  @ViewChild('iframe', { static: false }) iframe: ElementRef;
  @ViewChild('activityFrame', { static: false }) activityFrame: ElementRef;
  @ViewChild('videoContainer', { static: false }) videoContainer!: ElementRef;
  @ViewChild('tiktokFrame', { static: false }) tiktokFrame?: ElementRef<HTMLIFrameElement>;

  protected hasLockedFbIframePointerEvents = false;

  private destroyed = false;
  private pausedByVisibility = false;
  private hasCompletedCurrentCountDown = false;
  private visibilityChangeHandler?: () => void;
  private subscription: Subscription | null = null;
  protected unsubscribeAll: Subject<any> = new Subject<any>();
  private activeCountDownField: 'media' | 'lead' | null = null;
  private _fbXfbmlReadyHandler: ((msg: any) => void) | null = null;
  private resumeFieldAfterVisibility: 'media' | 'lead' | null = null;
  private tiktokMessageHandler: ((event: MessageEvent) => void) | null = null;
  private facebookSdkLoadPromise: Promise<void> | null = null;

  darkMode;
  player: any;
  locale: any;
  icon: string;
  interval: any;
  timeLeft: any;
  passedTime = 0;
  headerImagePath;
  stars: number[];
  mediaVideo: any;
  videoId: string;
  apis: any[] = [];
  progressBarColor;
  fbVideoLink = '';
  fbEmbedWidth = 0;
  games: any[] = [];
  fbEmbedHeight = 0;
  watched: any[] = [];
  minutes: number = 0;
  seconds: number = 0;
  elapsedTimeInMs = 0;
  progressValue: number;
  fbVideoElementId = '';
  performForm: FormGroup;
  hasAggregator: boolean;
  watchedTime: number = 0;
  activityForm: FormGroup;
  isLastActivity: boolean;
  isQuestPreview: boolean;
  questsIcon: PictureType;
  isVideoLoading: boolean;
  facebookPlayer: any = null;
  isPlaying: boolean = false;
  selectedRating: any[] = [];
  totalTimeInSeconds: number;
  fbUseIframeFallback = false;
  webpageUrl: SafeResourceUrl;
  rewardTimeText: string = '';
  _fbPlayRollback: any = null;
  protected _fbFallbackLoadTimeout: ReturnType<typeof setTimeout> | null = null;
  protected _fbFallbackIframeLoadHandler: (() => void) | null = null;
  protected fbFallbackLoadCompleted = false;
  hoverIndex: number[] = [-1];
  isLeadButtonDisabled = false;
  isMediaButtonDisabled = false;
  selectedEmojiIcon: any[] = [];
  selectedEmojiPicture: any[] = [];
  isQuestionsButtonDisabled = false;
  isMobile = window.innerWidth <= 767;
  dark$ = this.themeService.darkMode$;
  questActivities: QuestActivityType[];
  questsSlides = [1, 2, 3, 4, 5, 6, 7];
  quest: QuestPerformedType | QuestType;
  mediaSource: QuestActionDefinitionVideo;
  selectedQuestActivity: QuestActivityType;
  currentLevel: ReputationWithoutTargetType;
  questions: QuestionWithResponsesStatsType[];
  settings: boolean[] = Array(15).fill(false);
  fbFallbackSrc: SafeResourceUrl | null = null;
  params: QuestActionDefinitionDefinitionApiParamsType[];
  loadingActivitiesByQuest$ = this.questsService.loadingActivitiesByQuest$;
  widgetSettings$: Observable<WidgetIntegrationType> = this.reputationService.widgetSettings$;

  get questionsResponses(): FormArray {
    return this.activityForm?.get('responses') as FormArray;
  }

  get paramsArray(): FormArray {
    return this.performForm?.get(['api', 'params']) as FormArray;
  }

  constructor(
    protected router: Router,
    protected ngZone: NgZone,
    protected element: ElementRef,
    protected renderer: Renderer2,
    protected navCtrl: NavController,
    protected cd: ChangeDetectorRef,
    protected sanitizer: DomSanitizer,
    protected formBuilder: FormBuilder,
    protected translate: TranslateService,
    protected authService: MobileAuthService,
    protected toasterService: ToasterService,
    protected themeService: MobileThemeService,
    protected questsService: MobileQuestsService,
    protected reputationService: MobileReputationService,
    @Inject(DOCUMENT) protected document: Document,
  ) {
    this.questsService.quest$.pipe(takeUntil(this.unsubscribeAll)).subscribe((quest) => {
      if (quest) {
        this.quest = quest;
      } else {
        const nav = this.router.getCurrentNavigation();
        this.quest = nav?.extras?.state?.quest;
      }
    });
    this.questsService
      .getQuestActivitiesByQuest(this.quest.id)
      .pipe(
        switchMap((res) => {
          if (res?.[0]?.activity?.action?.definition?.form?.form?.id) {
            return this.questsService.getQuestionsByForm(res[0]?.activity?.action?.definition?.form?.form?.id);
          } else return of(null);
        }),
      )
      .subscribe();
  }

  ngOnInit() {
    this.visibilityChangeHandler = () => {
      if (this.document?.hidden) {
        this.pauseForVisibility();
      } else {
        this.resumeAfterVisibility();
      }
    };
    this.document?.addEventListener?.('visibilitychange', this.visibilityChangeHandler);

    this.questsService.questActivitesByQuest$.pipe(takeUntil(this.unsubscribeAll)).subscribe((questActivitesByQuest) => {
      this.questActivities = questActivitesByQuest;
      if (this.questActivities?.length) {
        this.questsService.selectedQuestActivity$ = this.selectedQuestActivity = this.questActivities[0];
      }
    });
    this.questsService.questions$.pipe(takeUntil(this.unsubscribeAll)).subscribe((questions) => {
      this.questions = this.selectedQuestActivity?.activity?.action?.definition?.form?.random ? shuffle(questions) : questions;
      this.activityForm = this.formBuilder.group({
        responses: this.formBuilder.array(
          this.questions?.length
            ? map(this.questions, (qst, index) => {
                if (qst?.settings?.rating?.levels?.length) {
                  this.selectedRating[index] = 0;
                }
                if (qst?.settings?.smiley?.levels?.[0]?.picture?.path) {
                  this.selectedEmojiPicture[index] = qst?.settings?.smiley?.levels?.[0]?.picture;
                } else if (qst?.settings?.smiley?.levels?.[0]?.icon) {
                  this.selectedEmojiIcon[index] = qst?.settings?.smiley?.levels?.[0]?.icon;
                }
                return this.formBuilder.group({
                  question: [qst],
                  answers: this.formBuilder.group({
                    paragraph: ['', [Validators.minLength(qst?.settings?.paragraph?.min), Validators.maxLength(qst?.settings?.paragraph?.max)]],
                    shortAnswer: ['', [Validators.minLength(qst?.settings?.shortAnswer?.min), Validators.maxLength(qst?.settings?.shortAnswer?.max)]],
                    rating: [1],
                    number: ['', [Validators.min(qst?.settings?.number?.minValue), Validators.max(qst?.settings?.number?.maxValue)]],
                    singleChoice: [qst?.choices?.[0]?.item || ''],
                    date: this.formBuilder.group({
                      from: [''],
                      to: [''],
                    }),
                    multipleChoices: this.formBuilder.array(
                      qst?.choices?.length
                        ? map(qst?.choices, (choice, j: number) => {
                            return this.formBuilder.control(j === 0 ? choice?.item : '');
                          })
                        : [this.formBuilder.control(qst?.choices?.[0]?.item || '')],
                    ),
                    smiley: this.formBuilder.group({
                      icon: [qst?.settings?.smiley?.levels?.[0]?.icon || ''],
                      picture: this.formBuilder.group({
                        path: [qst?.settings?.smiley?.levels?.[0]?.picture?.baseUrl || ''],
                        baseUrl: [qst?.settings?.smiley?.levels?.[0]?.picture?.path || ''],
                      }),
                    }),
                  }),
                });
              })
            : [],
        ),
      });
    });
    this.questsService.selectedQuestActivity$.pipe(takeUntil(this.unsubscribeAll)).subscribe((activity) => {
      this.selectedQuestActivity = activity;
      if (activity) {
        if (this.selectedQuestActivity.activity?.action?.definition?.form?.form?.id) {
          this.activityForm.reset();
          this.questsService.questions$ = null;
          this.questsService.getQuestionsByForm(this.selectedQuestActivity.activity?.action?.definition?.form?.form?.id).subscribe();
        }
        this.isLastActivity = this.selectedQuestActivity?.id === this.questActivities[this.questActivities?.length - 1]?.id;
      }
      this.stopCountDown();
      if (activity?.activity?.action?.activityType?.code === 'API_ACTION') {
        this.params = filter(activity?.activity?.action?.definition?.api?.params, (item) => item?.user?.enable);
        this.performForm = this.formBuilder.group({
          api: this.formBuilder.group({
            activity: [''],
            params: this.formBuilder.array(
              this.params?.length
                ? map(this.params, (param) => {
                    return this.formBuilder.group({
                      key: [param?.key],
                      value: [param?.user?.label],
                      performValue: [],
                    });
                  })
                : [
                    this.formBuilder.group({
                      key: [],
                      value: [],
                      performValue: [],
                    }),
                  ],
            ),
          }),
        });
        const paramsFormArray = this.paramsArray;
        paramsFormArray.clear();
        map(this.params, (item) => {
          const paramGroup = this.formBuilder.group({
            key: [item?.key],
            value: [item?.user?.label],
            performValue: [],
          });
          paramsFormArray.push(paramGroup);
        });
      }
      if (this.selectedQuestActivity?.activity?.action?.activityType?.code === 'LEAD_GENERATION') {
        this.hasCompletedCurrentCountDown = false;
        this.progressValue = 0.5;
        this.passedTime = 0;
        this.elapsedTimeInMs = 0;
        this.webpageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.selectedQuestActivity?.activity?.action?.definition?.lead?.url);
        this.totalTimeInSeconds = this.timeLeft = this.selectedQuestActivity?.activity?.action?.definition?.lead?.minSeconds;
        this.updateTime(this.timeLeft);
        setTimeout(() => {
          if (this.activityFrame && this.activityFrame.nativeElement) {
            this.activityFrame.nativeElement.onload = () => {
              this.getCountDown('lead');
              this.startTrackingTime();
            };
          }
        });
      }
      if (this.selectedQuestActivity?.activity?.action?.activityType?.code === 'MEDIA') {
        this.hasCompletedCurrentCountDown = false;
        this.progressValue = 0.5;
        this.passedTime = 0;
        this.elapsedTimeInMs = 0;
        this.mediaSource = this.selectedQuestActivity?.activity?.action?.definition?.video?.source;
        const videoLink = this.selectedQuestActivity?.activity?.action?.definition?.video?.link;
        if (
          this.mediaSource === QuestActionDefinitionVideo.YOUTUBE ||
          this.mediaSource === QuestActionDefinitionVideo.YOUTUBE_SHORT ||
          this.mediaSource === QuestActionDefinitionVideo.TIKTOK_REEL ||
          this.mediaSource === QuestActionDefinitionVideo.FACEBOOK_REEL ||
          this.mediaSource === QuestActionDefinitionVideo.FACEBOOK_VIDEO
        ) {
          this.mediaVideo = this.setVideoLink(videoLink);
          if (this.mediaSource === QuestActionDefinitionVideo.FACEBOOK_REEL || this.mediaSource === QuestActionDefinitionVideo.FACEBOOK_VIDEO) {
            this.cd.detectChanges();
            setTimeout(() => this.addFacebookVideo(videoLink), 0);
          } else if (this.mediaSource === QuestActionDefinitionVideo.TIKTOK_REEL) {
            if (this.tiktokMessageHandler) {
              window.removeEventListener('message', this.tiktokMessageHandler);
            }
            this.tiktokMessageHandler = (event: MessageEvent) => {
              const data = event.data;
              if (typeof data !== 'object') return;
              if (data.type === 'onStateChange') {
                this.updateTiktokStatus(data.value);
              }
            };
            window.addEventListener('message', this.tiktokMessageHandler);
          }
        } else this.mediaVideo = videoLink;
        this.totalTimeInSeconds = this.timeLeft = this.selectedQuestActivity?.activity?.action?.definition?.video?.minSeconds;
        this.updateTime(this.timeLeft);
        if (this.mediaSource === QuestActionDefinitionVideo.PICTURE) {
          this.onMediaPlay();
        }
        this.cd.markForCheck();
      }
    });
  }

  ngAfterViewInit() {
    this.reputationService.findLoyaltySettingsByTargetOrAggregator(this.quest?.target?.pos?.id).subscribe((res) => {
      this.hasAggregator = !!res?.aggregator?.target?.pos?.id;
    });
  }

  selectActivity(activity) {
    this.questsService.selectedQuestActivity$ = activity;
  }

  getCountDown(field: string) {
    if (field === 'media' && !this.isPlaying) {
      return;
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.activeCountDownField = field === 'lead' ? 'lead' : 'media';
    this.isPlaying = field === 'media';
    this.hasCompletedCurrentCountDown = false;
    const timer$ = interval(100); // Update every 100ms for smoother progress
    const totalTimeInMs = this.totalTimeInSeconds * 1000; // Convert total time to milliseconds

    this.subscription = timer$.pipe(takeUntil(this.unsubscribeAll)).subscribe(() => {
      this.ngZone.run(() => {
        if (this.destroyed) return;
        if (this.elapsedTimeInMs < totalTimeInMs) {
          this.elapsedTimeInMs += 100; // Increment elapsed time by 100ms
          this.timeLeft = Math.ceil((totalTimeInMs - this.elapsedTimeInMs) / 1000); // Update time left in seconds
          this.updateTime(this.timeLeft);
          this.progressValue = (this.elapsedTimeInMs / totalTimeInMs) * 100;
          this.passedTime = this.totalTimeInSeconds - this.timeLeft;
          this.cd.markForCheck();
        } else {
          if (this.hasCompletedCurrentCountDown) {
            return;
          }
          this.hasCompletedCurrentCountDown = true;
          this.timeLeft = 0;
          this.passedTime = this.totalTimeInSeconds;
          this.updateTime(this.timeLeft);
          this.subscription?.unsubscribe();
          this.isPlaying = false;
          this.progressValue = 100;
          this.activeCountDownField = null;
          this.cd.markForCheck();
          if (this.destroyed) return;
          if (!this.isLastActivity) {
            this.navigateNextSlide(field);
          } else {
            this.save(field);
          }
        }
      });
    });
  }

  updateTiktokStatus(state: any) {
    if (state === 1) {
      this.onMediaPlay();
    } else if (state === 2) {
      this.onVideoPause();
    } else if (state === 0) {
      this.onVideoPause();
      this.save();
      this.cd.detectChanges();
    }
  }

  
  addFacebookVideo(link: string) {
    const facebookEmbedHref = this.getFacebookEmbedHref(link);
    const { width, height } = this.getFacebookVideoDimensions();

    this.isVideoLoading = true;
    this.facebookPlayer = null;
    this.hasLockedFbIframePointerEvents = false;
    this.fbUseIframeFallback = false;
    this.fbFallbackLoadCompleted = false;
    this.clearFbFallbackLoadListeners();
    this.fbFallbackSrc = null;
    this.fbVideoLink = facebookEmbedHref;
    this.fbEmbedWidth = width;
    this.fbEmbedHeight = height;
    this.fbVideoElementId = `fb-video-${Date.now()}-${Math.floor(Math.random() * 100000)}`;

    let xfbmlReadyFired = false;

    if (this._fbXfbmlReadyHandler) {
      try {
        (window as any).FB?.Event?.unsubscribe('xfbml.ready', this._fbXfbmlReadyHandler);
      } catch (e) {}
      this._fbXfbmlReadyHandler = null;
    }

    this.cd.detectChanges();

    const findWrapper = (): HTMLElement | null => {
      const shadowRoot = this.element?.nativeElement?.shadowRoot;
      if (shadowRoot) {
        const shadowWrappers = shadowRoot.querySelectorAll('.fb-video-wrapper');
        if (shadowWrappers?.length) {
          return shadowWrappers[0] as HTMLElement;
        }
      }

      const localWrappers = this.element?.nativeElement?.querySelectorAll?.('.fb-video-wrapper');
      if (localWrappers?.length) {
        return localWrappers[0] as HTMLElement;
      }

      const documentWrappers = this.document?.querySelectorAll?.('.fb-video-wrapper');
      if (documentWrappers?.length) {
        return documentWrappers[0] as HTMLElement;
      }

      return null;
    };

    const ensureFBInitialized = (): boolean => {
      const FB = (window as any).FB;
      if (!FB || typeof FB.init !== 'function') {
        return false;
      }

      if (!FB.XFBML) {
        try {
          FB.init({
            appId: '278013208459559',
            autoLogAppEvents: true,
            xfbml: true,
            version: 'v22.0',
          });
        } catch (e) {
          return false;
        }
      }

      return !!(FB?.XFBML && typeof FB.XFBML.parse === 'function');
    };

    const syncFbVideoElement = (wrapper: HTMLElement): HTMLElement | null => {
      if (!wrapper) {
        return null;
      }

      wrapper.classList.remove('iframe-fallback');
      wrapper.querySelectorAll('.fb_iframe_widget').forEach((el) => el.remove());

      let fbVideoEl = wrapper.querySelector('.fb-video') as HTMLElement | null;
      if (!fbVideoEl) {
        fbVideoEl = this.document.createElement('div');
        fbVideoEl.className = 'fb-video';
        wrapper.insertBefore(fbVideoEl, wrapper.firstChild);
      }

      fbVideoEl.id = this.fbVideoElementId;
      fbVideoEl.setAttribute('data-width', width.toString());
      fbVideoEl.setAttribute('data-height', height.toString());
      fbVideoEl.setAttribute('data-href', facebookEmbedHref);
      fbVideoEl.setAttribute('data-allowfullscreen', 'false');
      fbVideoEl.setAttribute('data-show-text', 'false');
      fbVideoEl.setAttribute('data-autoplay', 'false');
      fbVideoEl.style.width = `${width}px`;
      fbVideoEl.style.maxWidth = '100%';
      fbVideoEl.style.minHeight = `${height}px`;
      wrapper.style.minHeight = `${height}px`;

      return fbVideoEl;
    };

    const renderIframeFallback = (wrapper: HTMLElement | null) => {
      wrapper?.querySelectorAll('.fb-video, .fb_iframe_widget').forEach((el) => el.remove());
      wrapper?.classList.add('iframe-fallback');

      const canonicalHref = this.getFacebookIframeEmbedHref(facebookEmbedHref);
      const isReel = this.mediaSource === QuestActionDefinitionVideo.FACEBOOK_REEL;
      const fallbackUrl = this.buildFacebookIframeFallbackUrl(facebookEmbedHref, width, height, isReel);

      this.ngZone.run(() => {
        this.fbUseIframeFallback = true;
        this.fbVideoLink = canonicalHref;
        this.fbFallbackSrc = this.sanitizer.bypassSecurityTrustResourceUrl(fallbackUrl);
        this.isVideoLoading = true;
        this.cd.detectChanges();
        this.attachFbFallbackIframeLoadListener(wrapper);
      });
    };

    const subscribeToPlayerEvents = (player: any) => {
      if (!player || typeof player.subscribe !== 'function') {
        return;
      }

      this.facebookPlayer = player;
      player.subscribe('startedPlaying', () => {
        this.ngZone.run(() => {
          if (this._fbPlayRollback) {
            clearTimeout(this._fbPlayRollback);
            this._fbPlayRollback = null;
          }
          this.isPlaying = true;
          if (!this.hasLockedFbIframePointerEvents) {
            const tryLockIframePointerEvents = (attempt = 0) => {
              if (this.hasLockedFbIframePointerEvents) {
                return;
              }

              this.hasLockedFbIframePointerEvents = this.disableFacebookIframePointerEvents();

              if (!this.hasLockedFbIframePointerEvents && attempt < 8) {
                setTimeout(() => tryLockIframePointerEvents(attempt + 1), 250);
              }
            };

            tryLockIframePointerEvents();
          }
          this.onMediaPlay();
          this.cd.detectChanges();
        });
      });
      player.subscribe('paused', () => {
        this.ngZone.run(() => {
          if (this._fbPlayRollback) {
            clearTimeout(this._fbPlayRollback);
            this._fbPlayRollback = null;
          }
          this.isPlaying = false;
          this.onVideoPause();
          this.cd.detectChanges();
        });
      });
      player.subscribe('finishedPlaying', () => {
        this.ngZone.run(() => {
          if (this._fbPlayRollback) {
            clearTimeout(this._fbPlayRollback);
            this._fbPlayRollback = null;
          }
          this.isPlaying = false;
          this.onVideoPause();
          this.save();
          this.cd.detectChanges();
        });
      });
    };

    const checkRenderSuccess = (wrapper: HTMLElement): boolean => {
      const fbVideoEl = wrapper?.querySelector('.fb-video');
      if (!fbVideoEl) {
        return false;
      }

      const renderedIframe = fbVideoEl.querySelector('iframe') || wrapper.querySelector('.fb_iframe_widget iframe');
      return !!renderedIframe && (renderedIframe as HTMLElement).getClientRects?.().length > 0;
    };

    const parseAndSubscribe = (wrapper: HTMLElement) => {
      const FB = (window as any).FB;
      const fbVideoEl = syncFbVideoElement(wrapper);

      if (!fbVideoEl || !FB?.XFBML?.parse) {
        renderIframeFallback(wrapper);
        return;
      }

      if (this._fbXfbmlReadyHandler) {
        try {
          FB.Event.unsubscribe('xfbml.ready', this._fbXfbmlReadyHandler);
        } catch (e) {}
        this._fbXfbmlReadyHandler = null;
      }

      const handleXfbmlReady = (msg: any) => {
        if (xfbmlReadyFired || msg?.type !== 'video' || !msg?.instance) {
          return;
        }

        if (msg.id && msg.id !== this.fbVideoElementId) {
          return;
        }

        xfbmlReadyFired = true;
        this._fbXfbmlReadyHandler = null;
        try {
          FB.Event.unsubscribe('xfbml.ready', handleXfbmlReady);
        } catch (e) {}

        this.ngZone.run(() => {
          this.isVideoLoading = false;
          subscribeToPlayerEvents(msg.instance);
          this.cd.detectChanges();
        });
      };

      this._fbXfbmlReadyHandler = handleXfbmlReady;

      try {
        FB.Event.subscribe('xfbml.ready', handleXfbmlReady);
      } catch (e) {}

      try {
        FB.XFBML.parse(fbVideoEl.parentElement || undefined);
      } catch (e) {}

      setTimeout(() => {
        if (xfbmlReadyFired) {
          return;
        }

        if (checkRenderSuccess(wrapper)) {
          this.ngZone.run(() => {
            this.isVideoLoading = false;
            this.cd.detectChanges();
          });
          return;
        }

        renderIframeFallback(wrapper);
      }, 4000);
    };

    const waitForWrapperAndParse = (attempt = 0) => {
      const wrapper = findWrapper();

      if (wrapper && ensureFBInitialized()) {
        setTimeout(() => parseAndSubscribe(wrapper), 100);
        return;
      }

      if (attempt >= 30) {
        renderIframeFallback(wrapper);
        return;
      }

      setTimeout(() => waitForWrapperAndParse(attempt + 1), 200);
    };

    this.ensureFacebookSdkLoaded()
      .then(() => waitForWrapperAndParse())
      .catch(() => renderIframeFallback(findWrapper()));
  }

  protected ensureFacebookSdkLoaded(): Promise<void> {
    if (this.facebookSdkLoadPromise) {
      return this.facebookSdkLoadPromise;
    }

    this.facebookSdkLoadPromise = new Promise((resolve, reject) => {
      const win = window as any;
      const fbScriptUrl = 'https://connect.facebook.net/en_US/sdk.js';

      const tryResolveWhenReady = (): boolean => {
        const FB = win.FB;
        if (!FB || typeof FB.init !== 'function') {
          return false;
        }

        try {
          if (!FB.XFBML) {
            FB.init({
              appId: '278013208459559',
              autoLogAppEvents: true,
              xfbml: true,
              version: 'v22.0',
            });
          }
        } catch (e) {
          return false;
        }

        if (FB?.XFBML && typeof FB.XFBML.parse === 'function') {
          resolve();
          return true;
        }

        return false;
      };

      if (tryResolveWhenReady()) {
        return;
      }

      const previousInit = win.fbAsyncInit;
      win.fbAsyncInit = () => {
        try {
          previousInit?.();
        } catch (e) {
          /* ignore */
        }

        if (tryResolveWhenReady()) {
          return;
        }

        let attempts = 0;
        const poll = () => {
          if (tryResolveWhenReady()) {
            return;
          }

          if (++attempts >= 50) {
            reject(new Error('Facebook SDK failed to initialize'));
            return;
          }

          setTimeout(poll, 100);
        };

        poll();
      };

      if (!this.document.getElementById('fb-root')) {
        const fbRoot = this.document.createElement('div');
        fbRoot.id = 'fb-root';
        this.document.body.appendChild(fbRoot);
      }

      let script =
        (this.document.getElementById('facebook-jssdk') as HTMLScriptElement | null) ||
        (this.document.querySelector(`script[src="${fbScriptUrl}"]`) as HTMLScriptElement | null);

      if (script) {
        let attempts = 0;
        const pollExistingScript = () => {
          if (tryResolveWhenReady()) {
            return;
          }

          if (++attempts >= 50) {
            reject(new Error('Facebook SDK failed to initialize'));
            return;
          }

          setTimeout(pollExistingScript, 100);
        };

        pollExistingScript();
        return;
      }

      script = this.document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = fbScriptUrl;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.onerror = () => reject(new Error('Facebook SDK script failed to load'));
      script.onload = () => {
        if (tryResolveWhenReady()) {
          return;
        }

        let attempts = 0;
        const pollAfterLoad = () => {
          if (tryResolveWhenReady()) {
            return;
          }

          if (++attempts >= 50) {
            reject(new Error('Facebook SDK failed to initialize after load'));
            return;
          }

          setTimeout(pollAfterLoad, 100);
        };

        pollAfterLoad();
      };

      const firstScript = this.document.getElementsByTagName('script')[0];
      if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        this.document.body.appendChild(script);
      }
    });

    return this.facebookSdkLoadPromise;
  }

  protected normalizeFacebookVideoLink(link: string): string {
    if (!link?.trim()) {
      return link;
    }

    let current = link.trim().replace(/^https?:\/\/m\.facebook\.com/i, 'https://www.facebook.com');

    for (let depth = 0; depth < 5; depth++) {
      if (!/facebook\.com\/plugins\/video\.php/i.test(current)) {
        break;
      }

      try {
        const url = new URL(current);
        const href = url.searchParams.get('href');
        if (!href) {
          break;
        }
        current = decodeURIComponent(href);
      } catch {
        break;
      }
    }

    for (let i = 0; i < 3; i++) {
      try {
        const decoded = decodeURIComponent(current);
        if (decoded === current) {
          break;
        }
        current = decoded;
      } catch {
        break;
      }
    }

    return current;
  }

  protected isLocalDevelopmentHost(): boolean {
    const host = window.location?.hostname || '';
    return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
  }

  protected getFacebookIframeEmbedHref(link: string): string {
    const normalizedLink = this.normalizeFacebookVideoLink(link);
    const extracted = this.extractVideoId(normalizedLink);

    if (extracted.platform === 'facebook' && extracted.id) {
      if (extracted.type === 'reel') {
        return `https://www.facebook.com/reel/${extracted.id}/`;
      }

      return `https://www.facebook.com/watch/?v=${extracted.id}`;
    }

    return normalizedLink;
  }

  protected buildFacebookIframeFallbackUrl(
    link: string,
    displayWidth: number,
    displayHeight: number,
    isReel = false,
  ): string {
    const canonicalHref = this.getFacebookIframeEmbedHref(link);

    if (/plugins\/video\.php/i.test(canonicalHref)) {
      throw new Error('Invalid Facebook iframe href: plugins URL cannot be embedded');
    }

    const embedWidth = Math.min(Math.max(280, Math.round(displayWidth)), 560);
    const embedHeight = isReel
      ? Math.min(Math.round(embedWidth * (16 / 9)), 720)
      : Math.min(Math.max(158, Math.round(displayHeight)), Math.round(embedWidth * 0.75));

    const params = new URLSearchParams({
      href: canonicalHref,
      show_text: 'false',
      width: String(embedWidth),
      height: String(embedHeight),
      allowfullscreen: 'true',
    });

    if (!this.isLocalDevelopmentHost()) {
      params.set('appId', '278013208459559');
    }

    return `https://www.facebook.com/plugins/video.php?${params.toString()}`;
  }

  protected getFacebookEmbedHref(link: string): string {
    const normalizedLink = this.normalizeFacebookVideoLink(link);
    const extracted = this.extractVideoId(normalizedLink);

    if (extracted.platform === 'facebook' && extracted.id) {
      if (extracted.type === 'reel') {
        return `https://www.facebook.com/reel/${extracted.id}/`;
      }

      const pageVideoMatch = normalizedLink.match(/facebook\.com\/([^/?#]+)\/videos\/(\d+)/i);
      if (pageVideoMatch?.[1] && pageVideoMatch[1] !== 'plugins') {
        return `https://www.facebook.com/${pageVideoMatch[1]}/videos/${extracted.id}/`;
      }

      return `https://www.facebook.com/watch/?v=${extracted.id}`;
    }

    return normalizedLink;
  }

  protected getFacebookVideoDimensions(): { width: number; height: number } {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let width: number;
    let height: number;

    if (viewportWidth <= 767) {
      width = Math.round(viewportWidth);
      height = Math.round(viewportHeight * 0.60);
    } else if (viewportWidth <= 1024) {
      width = Math.round(viewportWidth);
      height = Math.round(viewportHeight * 0.65);
    } else {
      width = Math.round(Math.min(viewportWidth * 0.85, 720));
      height = Math.round((width * 9) / 16);
      const maxHeight = Math.round(Math.min(viewportHeight * 0.55, 620));
      if (height > maxHeight) {
        height = maxHeight;
        width = Math.round((height * 16) / 9);
      }
    }

    width = Math.max(280, width);

    return { width, height };
  }

  onFbFallbackIframeLoad(): void {
    this.completeFbFallbackLoad();
  }

  protected attachFbFallbackIframeLoadListener(wrapper: HTMLElement | null): void {
    this.clearFbFallbackLoadListeners();

    const tryAttach = (attempt = 0) => {
      const iframe =
        (wrapper?.querySelector('.fb-fallback-iframe') as HTMLIFrameElement | null) ||
        this.findFbFallbackIframeElement();

      if (!iframe) {
        if (attempt < 50) {
          this._fbFallbackLoadTimeout = setTimeout(() => tryAttach(attempt + 1), 100);
        } else {
          this.completeFbFallbackLoad();
        }
        return;
      }

      const onIframeLoad = () => this.completeFbFallbackLoad();
      this._fbFallbackIframeLoadHandler = onIframeLoad;
      iframe.addEventListener('load', onIframeLoad, { once: true });

      this._fbFallbackLoadTimeout = setTimeout(() => {
        if (!this.fbFallbackLoadCompleted && this.fbUseIframeFallback) {
          this.completeFbFallbackLoad();
        }
      }, 20000);
    };

    tryAttach();
  }

  protected findFbFallbackIframeElement(): HTMLIFrameElement | null {
    const roots = [this.element?.nativeElement?.shadowRoot, this.element?.nativeElement, this.document].filter(
      Boolean,
    ) as ParentNode[];

    for (const root of roots) {
      const iframe = root.querySelector?.('.fb-fallback-iframe') as HTMLIFrameElement | null;
      if (iframe) {
        return iframe;
      }
    }

    return null;
  }

  protected completeFbFallbackLoad(): void {
    if (this.fbFallbackLoadCompleted || !this.fbUseIframeFallback) {
      return;
    }

    this.fbFallbackLoadCompleted = true;
    this.clearFbFallbackLoadListeners();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.ngZone.run(() => {
          this.isVideoLoading = false;
          this.onMediaPlay();
          this.cd.detectChanges();
        });
      });
    });
  }

  protected clearFbFallbackLoadListeners(): void {
    if (this._fbFallbackLoadTimeout) {
      clearTimeout(this._fbFallbackLoadTimeout);
      this._fbFallbackLoadTimeout = null;
    }

    if (this._fbFallbackIframeLoadHandler) {
      const iframe = this.findFbFallbackIframeElement();
      iframe?.removeEventListener('load', this._fbFallbackIframeLoadHandler);
      this._fbFallbackIframeLoadHandler = null;
    }
  }

  disableFacebookIframePointerEvents(): boolean {
    const iframeSelectors = [
      '.fb-video-wrapper iframe',
      '.fb-video iframe',
      '.fb_iframe_widget iframe',
      'iframe[data-testid^="fb:video"]',
      'iframe[title*="Facebook Social Plugin"]',
      'iframe[src*="facebook.com/"][src*="plugins/video.php"]',
    ];

    const collectRoots = (): ParentNode[] => {
      const queue = [this.element?.nativeElement, this.element?.nativeElement?.shadowRoot, this.document, this.document?.body].filter(
        (node): node is ParentNode => !!node,
      );

      const roots: ParentNode[] = [];
      const visited = new Set<ParentNode>();

      while (queue.length) {
        const current = queue.shift();
        if (!current || visited.has(current) || typeof (current as any).querySelectorAll !== 'function') {
          continue;
        }

        visited.add(current);
        roots.push(current);

        const elements = Array.from((current as any).querySelectorAll('*')) as HTMLElement[];
        elements.forEach((element) => {
          const shadowRoot = (element as any).shadowRoot as ShadowRoot | undefined;
          if (shadowRoot && !visited.has(shadowRoot)) {
            queue.push(shadowRoot);
          }
        });
      }

      return roots;
    };

    const roots = collectRoots();
    const iframes = roots.reduce((acc, root) => {
      if (!root?.querySelectorAll) {
        return acc;
      }

      iframeSelectors.forEach((selector) => {
        acc.push(...(Array.from(root.querySelectorAll(selector)) as HTMLIFrameElement[]));
      });

      return acc;
    }, [] as HTMLIFrameElement[]);

    const uniqueIframes = (Array.from(new Set(iframes)) as HTMLIFrameElement[]).filter((iframe) => !!iframe && !!iframe.parentElement);
    uniqueIframes.forEach((iframe) => {
      this.renderer.setStyle(iframe, 'pointer-events', 'none');
    });

    return uniqueIframes.length > 0;
  }

  onTikTokReady() {
    if (this.mediaSource === QuestActionDefinitionVideo.TIKTOK_REEL) {
      this.requestTikTokPlayback();
    }
  }

  private _rollbackPlay() {
    if (this._fbPlayRollback) {
      clearTimeout(this._fbPlayRollback);
      this._fbPlayRollback = null;
    }
    if (this.facebookPlayer) {
      this.isPlaying = false;
      this.onVideoPause();
      this.cd.detectChanges();
    }
  }

  async onOverlayClick() {
    // optimistic UI: assume playback starts immediately
    this.isPlaying = true;
    this.onMediaPlay();
    this.cd.detectChanges();

    // If we have a FB player instance, try to start it and set a rollback
    if (this.facebookPlayer && typeof this.facebookPlayer.play === 'function') {
      try {
        const playResult: any = this.facebookPlayer.play();
        if (playResult && typeof playResult.then === 'function') {
          playResult.catch(() => this._rollbackPlay());
        }
      } catch (e) {
        this._rollbackPlay();
      }
      // rollback if SDK doesn't emit startedPlaying within 3s
      this._fbPlayRollback = setTimeout(() => this._rollbackPlay(), 3000);
    } else {
      // No FB instance yet: keep optimistic state (user likely interacts with iframe)
    }
  }

  private requestTikTokPlayback(attempt = 0) {
    const maxAttempts = 5;
    const iframe = this.tiktokFrame?.nativeElement;
    if (!iframe?.contentWindow) {
      if (attempt < maxAttempts) {
        setTimeout(() => this.requestTikTokPlayback(attempt + 1), 300);
      }
      return;
    }
    const commands = [
      { type: 'command', command: 'play' },
      { event: 'command', func: 'playVideo', args: [] },
    ];
    commands.forEach((message) => {
      try {
        iframe.contentWindow?.postMessage(message, '*');
      } catch {
        /* Ignore postMessage errors; retry handles transient cases */
      }
    });
  }

  redirectToPage(url: string) {
    Browser.open({ url });
  }

  onPlayerStateChange(event: any) {
    const playing = YT?.PlayerState?.PLAYING ?? 1;
    const paused = YT?.PlayerState?.PAUSED ?? 2;
    const ended = YT?.PlayerState?.ENDED ?? 0;
    if (event.data === playing) {
      this.onMediaPlay();
    } else if (event.data === paused) {
      this.onVideoPause();
    } else if (event.data === ended) {
      this.onVideoPause();
      this.save();
      this.cd.detectChanges();
    }
  }

  dateRangeValid(dateControl?: AbstractControl) {
    if (dateControl.get('from').value !== '' && dateControl.get('to').value !== '') {
      if (isAfter(dateControl.get('to').value, dateControl.get('from').value)) {
        return true;
      }
      return false;
    }
    return true;
  }

  getChoicePercentage(question: any, choice: string): number {
    const response = question.responses.find((res: any) => res.answer === choice);
    return response ? response : 0;
  }

  onArgsUpdate(arg: any) {
    this.games.push(arg.game);
  }

  navigateNextSlide(field?: string) {
    this.checkFields(field);
    let index = findIndex(this.questActivities, (item) => item?.id === this.selectedQuestActivity?.id);
    this.questsService.selectedQuestActivity$ = this.selectedQuestActivity = this.questActivities[index + 1];
    this.cd.detectChanges();
  }

  checkFields(field: string) {
    if (field === 'media') {
      this.watched.push({
        activity: this.selectedQuestActivity?.id,
        watched: this.watchedTime,
      });
      this.resetTime();
    }
    if (field === 'lead') {
      this.watched.push({
        activity: this.selectedQuestActivity?.id,
        watched: this.watchedTime,
      });
      this.resetTime();
    }
    if (field === 'api') {
      const params = map(this.paramsArray.value, (param) => {
        return {
          key: param?.key,
          value: param?.performValue,
        };
      }).filter((item) => item?.key && item?.value);
      this.apis.push({
        activity: this.selectedQuestActivity?.id,
        ...(params?.length ? { params } : {}),
      });
    }
  }

  save(field?: string, arg?: any) {
    if (field === 'game') {
      this.games.push(arg.game);
    }
    this.checkFields(field);
    let responses;
    if (this.activityForm.get('responses').value?.length) {
      let answers;
      responses = map(this.activityForm.get('responses').value, (res) => {
        answers = {
          ...(res.question?.type === QuestionTypeEnum.SINGLE_CHOICE ? { singleChoice: res?.answers?.singleChoice } : {}),
          ...(res.question?.type === QuestionTypeEnum.MULTIPLE_CHOICE
            ? { multipleChoices: filter(res?.answers?.multipleChoices, (choice) => choice?.trim() !== '') }
            : {}),
          ...(res.question?.type === QuestionTypeEnum.SMILEY
            ? {
                smiley: {
                  ...(res?.answers?.smiley?.picture?.baseUrl ? { picture: res?.answers?.smiley?.picture } : { icon: res?.answers?.smiley?.icon }),
                },
              }
            : {}),
          ...(res.question?.type === QuestionTypeEnum.DATETIME
            ? {
                date: {
                  from: res?.answers?.date?.from,
                  ...(res?.answers?.date?.to ? { to: res?.answers?.date?.to } : {}),
                },
              }
            : {}),
          ...(res.question?.type === QuestionTypeEnum.NUMBER ? { number: res?.answers?.number } : {}),
          ...(res.question?.type === QuestionTypeEnum.RATING ? { rating: res?.answers?.rating } : {}),
          ...(res.question?.type === QuestionTypeEnum.SHORT_ANSWER ? { shortAnswer: res?.answers?.shortAnswer } : {}),
          ...(res.question?.type === QuestionTypeEnum.PARAGRAPH ? { paragraph: res?.answers?.paragraph } : {}),
        };
        return {
          question: res?.question?.id,
          ...(keys(answers).length ? { answers } : {}),
          target: { pos: localStorage.getItem('posId') },
        };
      });
    }
    const input: any = {
      quest: this.quest?.id,
      ...(this.watched?.length ? { watched: this.watched } : {}),
      ...(this.apis?.length ? { api: this.apis } : {}),
      ...(this.games?.length ? { game: this.games } : {}),
      ...(responses?.length ? { responses } : {}),
    };
    this.handleQuestPayload(input);
    this.isMediaButtonDisabled = true;
    this.isLeadButtonDisabled = true;
    this.isQuestionsButtonDisabled = true;

    const handleResult = (res: any) => {
      if (res) {
        this.modalSuccess();
        this.navCtrl.navigateRoot('/quests');
        return this.reputationService.getCurrentUserQuantitativeWallets();
      }
      return of(null);
    };

    const handleError = (error: any) => {
      this.questsService.loadingActivitiesByQuest$ = false;
      const exception = error?.graphQLErrors?.[0]?.extensions?.exception?.response?.error;
      if (includes(exception, 'Quest already performed')) {
        this.modalError(this.translate.instant('QUEST_ALREADY_PERFORMED'));
      } else {
        this.modalError(this.translate.instant('STH_WENT_WRONG'));
      }
      return of(null);
    };
    let submit$;
    if (!this.hasAggregator) {
      submit$ = this.questsService.performNonPredefinedQuestByUser(input);
    } else {
      submit$ = this.authService.linkUserAccountForAggregator(this.quest?.target?.pos?.id).pipe(
        switchMap((res) => {
          localStorage.setItem('userToken', res?.accessToken);
          return this.questsService.performNonPredefinedQuestByUser(input);
        }),
      );
    }
    submit$.pipe(catchError(handleError), switchMap(handleResult)).subscribe(() => this.cd.markForCheck());
  }

  onMediaPlay() {
    if (this.hasCompletedCurrentCountDown) {
      return;
    }
    this.isPlaying = true;
    this.getCountDown('media');
    this.startTrackingTime();
    this.cd.markForCheck();
  }

  onVideoPause() {
    this.isPlaying = false;
    this.stopCountDown();
    this.stopTrackingTime();
    this.cd.markForCheck();
  }

  stopCountDown() {
    if (this.subscription) {
      this.subscription?.unsubscribe();
      this.subscription = null;
    }
    this.activeCountDownField = null;
  }

  startTrackingTime() {
    this.interval = setInterval(() => {
      this.ngZone.run(() => {
        this.watchedTime += 1;
        this.cd.markForCheck();
      });
    }, 1000);
  }

  stopTrackingTime() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  resetTime() {
    this.stopTrackingTime();
    this.watchedTime = 0;
  }

  setVideoLink(videoLink: string) {
    if (videoLink) {
      const { platform, id, type } = this.extractVideoId(videoLink);
      this.videoId = id;
      if (platform && id) {
        if (platform === 'youtube') {
          return this.sanitizer.bypassSecurityTrustResourceUrl(
            `https://www.youtube.com/embed/${id}?enablejsapi=1&autoplay=1&playsinline=1&controls=0&disablekb=1&fs=0&modestbranding=1&rel=0&iv_load_policy=3&showinfo=0`,
          );
        } else if (platform === 'tiktok') {
          return this.sanitizer.bypassSecurityTrustResourceUrl(
            `https://www.tiktok.com/player/v1/${id}?&music_info=0&rel=0&progress_bar=0&autoplay=0&play_button=0&native_context_menu=0&closed_caption=0&timestamp&description=0&volume_control=1&fullscreen_button=0`,
          );
        } else if (platform === 'facebook' && type === 'reel') {
          return this.sanitizer.bypassSecurityTrustResourceUrl(
            `https://www.facebook.com/plugins/video.php?href=https://www.facebook.com/reel/${id}&show_text=false`,
          );
        } else if (platform === 'facebook' && type === 'video') {
          return this.sanitizer.bypassSecurityTrustResourceUrl(
            `https://www.facebook.com/plugins/video.php?href=https://www.facebook.com/watch/?v=${id}&show_text=false`,
          );
        }
      }
    }
    return null;
  }

  extractVideoId(url: string): { platform: 'youtube' | 'tiktok' | 'facebook' | null; id: string | null; type?: 'reel' | 'video' } {
    if (!url?.trim()) {
      return { platform: null, id: null };
    }

    const candidate = this.normalizeFacebookVideoLink(url);

    // YouTube
    const youtubeRegex = /(?:youtu\.be\/|youtube\.com\/(?:.*(?:\/|v=|vi=|u\/\w\/|embed\/|shorts\/)))([^#\&\?]{11})/;
    const youtubeMatch = candidate.match(youtubeRegex);
    if (youtubeMatch && youtubeMatch[1].length === 11) {
      return { platform: 'youtube', id: youtubeMatch[1] };
    }

    // TikTok
    const tiktokRegex = /tiktok\.com\/@[\w.-]+\/video\/(\d+)/;
    const tiktokMatch = candidate.match(tiktokRegex);
    if (tiktokMatch && tiktokMatch[1]) {
      return { platform: 'tiktok', id: tiktokMatch[1] };
    }

    // Facebook Reels
    const facebookRegex = /facebook\.com\/reel\/(\d+)/;
    const facebookMatch = candidate.match(facebookRegex);
    if (facebookMatch && facebookMatch[1]) {
      return { platform: 'facebook', id: facebookMatch[1], type: 'reel' };
    }

    // common page video url: facebook.com/{page}/videos/{id}
    const facebookPageVideoRegex = /facebook\.com\/[\w\-\.]+\/videos\/(\d+)/i;
    const facebookPageVideoMatch = candidate.match(facebookPageVideoRegex);
    if (facebookPageVideoMatch && facebookPageVideoMatch[1]) {
      return { platform: 'facebook', id: facebookPageVideoMatch[1], type: 'video' };
    }

    // fallback: watch?v= id pattern
    const facebookVideoRegex = /facebook\.com\/watch\?v=(\d+)/;
    const facebookVideoMatch = candidate.match(facebookVideoRegex);
    if (facebookVideoMatch && facebookVideoMatch[1]) {
      return { platform: 'facebook', id: facebookVideoMatch[1], type: 'video' };
    }

    return { platform: null, id: null };
  }

  shareSocialMedia() {
    (window as any).fbAsyncInit();
    FB.ui(
      {
        display: 'popup',
        method: 'share_open_graph',
        action_type: 'og.likes',
        action_properties: JSON.stringify({
          object: this.selectedQuestActivity?.activity?.action?.definition?.socialMedia?.url,
        }),
      },
      (response) => {
        if (response && !response.error_message) {
          if (this.isLastActivity) {
            this.save();
          } else {
            this.navigateNextSlide();
          }
        } else {
          this.toasterService.info(this.translate.instant('FB_POST_NOT_SHARED'));
        }
      },
    );
  }

  updateTime(timeLeft: number) {
    this.minutes = Math.floor(timeLeft / 60);
    this.seconds = timeLeft % 60;
    this.updateRewardTimeText();
  }

  updateRewardTimeText(): void {
    if (this.minutes <= 0 && this.seconds <= 0) {
      this.rewardTimeText = '';
      return;
    }

    const timeStr = `${this.minutes > 0 ? this.minutes + ':' : ''}${this.seconds < 10 ? '0' + this.seconds : this.seconds}`;

    const unitKey = this.minutes === 1 ? 'MINUTE' : this.minutes > 1 ? 'MINUTES' : this.seconds === 1 ? 'SECOND' : 'SECONDS';

    this.rewardTimeText = `${this.translate.instant('REWARD_IN')} ${timeStr} ${this.translate.instant(unitKey)}`;
  }

  isCheckboxDisabled(control: AbstractControl) {
    return control.value.filter((item) => item.trim() !== '')?.length === 1;
  }

  isSelected(control: AbstractControl, item: string): boolean {
    const answers = control.value;
    return answers.includes(item);
  }

  onChangeMultipleChoice(checked: boolean, control: FormArray, choice: string, j: number) {
    if (checked) {
      control.at(j).patchValue(choice);
    } else {
      const index = findIndex(control.value, (item) => item === choice);
      control.at(index).patchValue('');
    }
  }

  starRange(num: number) {
    return range(num);
  }

  selectEmoji(control: AbstractControl, picture: any, field: string, index: number) {
    if (field === 'picture') {
      this.selectedEmojiPicture[index] = picture;
    } else {
      this.selectedEmojiIcon[index] = picture;
    }
    control.get(['answers', 'smiley', field]).patchValue(picture);
  }

  selectEmojiIcon(control: AbstractControl, picture: any, field: string) {
    this.selectedEmojiPicture = picture;
    control.get(['answers', 'smiley', field]).patchValue(picture);
  }

  onChangeChoice(item: string, control: AbstractControl) {
    control.get(['answers', 'singleChoice']).patchValue(item);
  }

  triggerSettingsDropdown(index: number) {
    this.settings[index] = !this.settings[index];
  }

  setRating(index: number, control: AbstractControl, j: number): void {
    this.selectedRating[index] = j;
    control.get(['answers', 'rating']).patchValue(j + 1);
  }

  clearHoverIndex(index: number): void {
    this.hoverIndex[index] = -1;
  }

  setHoverIndex(index: number, j: number): void {
    this.hoverIndex[index] = j;
  }

  modalSuccess() {
    const message = this.translate.instant('ACTIVITY_PERFORMED');
    this.toasterService.success(message, null, null, 8000);
  }

  modalError(text?: string) {
    this.toasterService.error(text || this.translate.instant('STH_WENT_WRONG'));
  }

  protected handleQuestPayload(payload: any): void {
    if (!payload) {
      return;
    }
    // Placeholder for actual quest submission logic; keeps payload referenced while integration is disabled.
  }

  private pauseForVisibility() {
    // If a countdown is currently in progress, pause it while tab is hidden.
    if (this.hasCompletedCurrentCountDown) {
      return;
    }

    const hadCountDownRunning = !!this.subscription;
    const hadTrackingRunning = !!this.interval;

    if (!hadCountDownRunning && !hadTrackingRunning) {
      return;
    }

    this.pausedByVisibility = true;
    this.resumeFieldAfterVisibility = this.activeCountDownField;
    this.stopCountDown();
    this.stopTrackingTime();
    // Pause the actual video players
    this.pauseFbVideos();
    this.cd.markForCheck();
  }

  private resumeAfterVisibility() {
    if (!this.pausedByVisibility) {
      return;
    }
    this.pausedByVisibility = false;

    if (this.hasCompletedCurrentCountDown) {
      this.resumeFieldAfterVisibility = null;
      return;
    }

    const field = this.resumeFieldAfterVisibility;
    this.resumeFieldAfterVisibility = null;
    if (!field) {
      return;
    }

    const activityCode = this.selectedQuestActivity?.activity?.action?.activityType?.code;
    if (field === 'lead' && activityCode === 'LEAD_GENERATION') {
      this.getCountDown('lead');
      this.startTrackingTime();
      return;
    }

    if (field === 'media' && activityCode === 'MEDIA') {
      // Ensure getCountDown('media') doesn't early-return.
      this.isPlaying = true;
      this.getCountDown('media');
      this.startTrackingTime();
      this.resumeFbVideos();
    }
  }

  private pauseFbVideos() {
    // Pause Facebook
    if (this.facebookPlayer && typeof this.facebookPlayer.pause === 'function') {
      try {
        this.facebookPlayer.pause();
      } catch (e) {
        /* ignore */
      }
    }
  }

  private resumeFbVideos() {
    // Resume Facebook
    if (this.facebookPlayer && typeof this.facebookPlayer.play === 'function') {
      try {
        this.facebookPlayer.play();
      } catch (e) {
        /* ignore */
      }
    }
  }
  ngOnDestroy() {
    this.destroyed = true;
    this.questsService.loadingActivitiesByQuest$ = false;
    this.questsService.selectedQuestActivity$ = null;
    this.activityForm.reset();
    this.questsService.questActivitesByQuest$ = null;
    this.questsService.questions$ = null;
    this.stopCountDown();
    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
    this.resetTime();
    this.elapsedTimeInMs = 0;
    this.minutes = 0;
    this.seconds = 0;
    this.progressValue = 0.5;
    this.passedTime = 0;
    this.mediaVideo = null;
    if (this.visibilityChangeHandler) {
      this.document?.removeEventListener?.('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = undefined;
    }
    if (this.tiktokMessageHandler) {
      window.removeEventListener('message', this.tiktokMessageHandler);
      this.tiktokMessageHandler = null;
    }
    if (this._fbPlayRollback) {
      clearTimeout(this._fbPlayRollback);
      this._fbPlayRollback = null;
    }
    this.clearFbFallbackLoadListeners();
  }
}
